import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { useState, useRef } from 'react';
import { arrayOf, bool, func, node, oneOfType, shape, string } from 'prop-types';
import { useOnClickOutside, useOnMouseHover } from '../../utils';
import { Container } from './dropdown.styles';
import DropdownContext from './dropdown-context';
import DropdownTitle from './dropdown-title';
import DropdownMenu from './dropdown-menu';
import DropdownLabel from './dropdown-label';
import DropdownItem from './dropdown-item';
import HorizontalRule from '../HorizontalRule';

const DropdownDivider = props => {
  return /*#__PURE__*/React.createElement(HorizontalRule, props);
};

const Dropdown = _ref => {
  let {
    title,
    children,
    options,
    onClick,
    onChange,
    mouseenter,
    pointing
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["title", "children", "options", "onClick", "onChange", "mouseenter", "pointing"]);

  const ref = useRef();
  const [isOpen, setIsOpen] = useState(false);

  if (mouseenter) {
    useOnMouseHover(ref, setIsOpen);
  } else {
    useOnClickOutside(ref, () => setIsOpen(false));
  }
  /**
   * Update onClick function when user click over [Dropdown.Title]
   * to open [Dropdown.Menu]
   *
   * @param  {} e
   */


  const handleOnClick = e => {
    /* istanbul ignore else */
    if (!mouseenter) setIsOpen(!isOpen);
    onClick(e);
  };
  /**
   * Update onChange function when user click on a [Dropdown.Item]
   *
   * @param  {} e
   * @param  {} {value}
   */


  const handleOnChange = (e, {
    value
  }) => {
    onChange(e, {
      value
    });
  };
  /**
   * Build [Dropdown.Items] when user send [options] props
   * @example [{ key: '', label: '' ...}, { key: '', label: '' ...}]
   */


  const buildByOptions = () => /*#__PURE__*/React.createElement(DropdownMenu, {
    mouseenter: mouseenter,
    pointing: pointing
  }, options == null ? void 0 : options.map(_ref2 => {
    let {
      key,
      text,
      value,
      startIcon,
      endIcon
    } = _ref2,
        others = _objectWithoutPropertiesLoose(_ref2, ["key", "text", "value", "startIcon", "endIcon"]);

    return /*#__PURE__*/React.createElement(DropdownItem, _extends({
      key: key,
      value: value,
      startIcon: startIcon,
      endIcon: endIcon
    }, others), text);
  }));
  /**
   * Build [Dropdown.Title] according to component structure
   *
   * There are 2 ways to use a title
   * @example <Dropdown title="dropdown"></Dropdown>
   *          <Dropdown title={<span>dropdown</span>}></Dropdown>
   *
   * @example <Dropdown>
   *            <Dropdown.Title>Dropdown</Dropdown.Title>
   *          </Dropdown>
   */


  const buildDropdownTitle = () => {
    const TitleComponent = React.Children.toArray(children).find(({
      type: {
        displayName
      }
    }) => displayName === 'DropdownTitle');
    if (TitleComponent) return /*#__PURE__*/React.isValidElement(TitleComponent) && TitleComponent;
    return /*#__PURE__*/React.createElement(DropdownTitle, props, title);
  };
  /**
   * Get only [Dropdown.Menu] from [children] whether user send it
   *
   * @example <Dropdown.Menu>
   *            <Dropdown.Item>Dropdown Item</Dropdown.Item>
   *          </Dropdown.Menu>
   */


  const buildDropdownMenu = () => {
    return React.Children.toArray(children).find(({
      type: {
        displayName
      }
    }) => displayName === 'DropdownMenu');
  };

  return /*#__PURE__*/React.createElement(DropdownContext, {
    value: {
      isOpen,
      setIsOpen,
      handleOnClick,
      handleOnChange,
      pointing
    }
  }, /*#__PURE__*/React.createElement(Container, {
    ref: ref,
    "data-testid": "dropdown"
  }, buildDropdownTitle(), options ? buildByOptions() : buildDropdownMenu()));
};

process.env.NODE_ENV !== "production" ? Dropdown.propTypes = {
  children: node,
  title: oneOfType([string, node]),
  options: arrayOf(shape(Object.assign({}, DropdownItem.propTypes))),
  mouseenter: bool,
  pointing: bool,
  onChange: func,
  onClick: func,
  as: string
} : void 0;
Dropdown.defaultProps = {
  children: null,
  mouseenter: false,
  pointing: false,
  onChange: () => {},
  onClick: () => {}
};
Dropdown.Title = DropdownTitle;
Dropdown.Menu = DropdownMenu;
Dropdown.Label = DropdownLabel;
Dropdown.Divider = DropdownDivider;
Dropdown.Item = DropdownItem;
DropdownDivider.displayName = 'DropdownDivider';
export default Dropdown;